// SSE client for the inbound-model-builder agent (rgip-copilot), reached through
// Studio's own same-origin proxy at /api/copilot/* (never rgip directly). The agent
// answers questions about an inbound model and points the user at a tree node via a
// `studio_jump` activity. It is no longer only a reader: the tree-editing tools land
// with rgip's `feat/safer-tree-edits`, and a tree write announces itself with a
// `tree_refresh` activity so the tree pane refetches.
//
// An answer is not only prose: the agent can embed deliverable blocks in it, which
// arrive as artifact_start/delta/end interleaved with the prose tokens (rgip ADR
// 0006). See chatParts.ts for why that interleaving is the whole reason a block or
// a pill can be rendered where the agent put it.
//
// Durable background runs (kind=chat): POST /v1/runs enqueues the turn on the rgip
// worker and returns a run_id immediately; the turn keeps running (and checkpoints
// to the thread) even if the client detaches.
//
// `findActiveRun` below is the half of that which was never wired: nothing calls it,
// on either surface, so a refresh mid-answer loses the stream and the answer arrives
// only in the transcript on the next load. Kept because the turn really does survive
// and re-attaching is a few lines at the call site, but said plainly here, because
// this comment used to claim the re-attach already happened.

import { legacyForced } from '../layout/legacy-mode';

const BASE = '/api/copilot';

/**
 * The agents whose threads are CONVERSATIONS, as opposed to automated runs: one per
 * model base. A tree based model is worked on by `inbound-model-builder`, a rule based
 * one by `rules-model-builder`, and a model switched between the two has had both.
 *
 * Named rather than left unfiltered because three callers here start a turn pinned to a
 * model: the chat, `model-summary` ("Summarize this model.") and `tree-diagnostics`
 * ("Review this model's decision trees."). All three write a thread row tagged with that
 * model, so a session list filtered on the model alone would offer somebody their last
 * diagnostics run as a conversation to continue.
 */
export const TREES_AGENT = 'inbound-model-builder';
export const RULES_AGENT = 'rules-model-builder';
const CHAT_AGENTS = [TREES_AGENT, RULES_AGENT];

/**
 * The agent for a model's base. Anything that is not `trees` scores with rules, the way
 * the scoring SQL reads it; a base not known yet reads as trees, which is what every
 * model got before rule based models had their own agent.
 */
export function agentForBase(base: string | null | undefined): string {
  return base && base !== 'trees' ? RULES_AGENT : TREES_AGENT;
}

/**
 * The chat agent for a model, or null when no agent can work on that kind of model yet.
 * Both builders know Customer Fit only: an engagement model (`pql`, `pql2`, `mql`, `mqa`)
 * scores event weights, and the tree builder answered one with tree advice on 2026-09-25.
 * A model not loaded yet has no type and gets no agent. When an engagement builder
 * exists, its model types map to it here, and the page mounts the chat for them.
 */
export function chatAgentFor(
  type: string | null | undefined,
  base: string | null | undefined
): string | null {
  return type === 'customer_fit' ? agentForBase(base) : null;
}

export async function errorMessage(response: Response): Promise<string> {
  const text = await response.text().catch(() => '');
  if (text) {
    try {
      const payload = JSON.parse(text);
      const detail = payload?.error?.message ?? payload?.detail;
      if (typeof detail === 'string') return detail;
    } catch {
      // not JSON
    }
  }
  return response.statusText || 'Request failed';
}

// A host-UI jump the agent surfaces (ADR 0010): point the Studio UI at something,
// e.g. a tree node, without the agent ever writing to the model. It rides the
// activity rail as a `studio_jump` activity, so it persists + replays like any step.
export interface UiActionEvent {
  target_type: string;
  target_id: string;
  label: string;
}

// The activity `key` studio_focus maps to (see rgip presentation.py). Both a live
// activity_start and a persisted step use it, so this one extractor serves both.
export const STUDIO_JUMP_KEY = 'studio_jump';

// The activity `key` the `refresh_summary` tool maps to (rgip presentation.py). The
// page regenerates the top-of-page summary when it sees this in the follow-up stream.
export const SUMMARY_REFRESH_KEY = 'summary_refresh';

// The activity `key` a tree writing tool maps to. The tree pane refetches the
// tree when it sees this, the same way the summary panel refetches on
// `summary_refresh`.
//
// Note the header of this file: the chat agent is read-only, and admin's
// `editorDispatch` already carries model_updated/model_edited for an agent that
// writes. A tree building agent is that case, so this key exists on the same
// footing rather than as a new mechanism.
export const TREE_REFRESH_KEY = 'tree_refresh';

// The activity `key` a signals writing tool maps to. The Review page reloads
// the signals when it sees this, the same way the tree pane reloads on
// `tree_refresh`. Signals are one document per model, so unlike the tree key
// this one names no target: there is nothing to choose between.
export const SIGNALS_REFRESH_KEY = 'signals_refresh';

// The activity `key` a model LIFECYCLE write maps to (rgip presentation.py, ADR 0020).
// The models list reloads when it sees this, the same way the tree pane reloads on
// `tree_refresh`. Like signals it names no target: the list is one page per tenant, so
// there is nothing to choose between.
export const MODELS_REFRESH_KEY = 'models_refresh';

// A rule based model's rules, written by the agent: the Rules page reloads them.
export const RULES_REFRESH_KEY = 'rules_refresh';

// One model's own settings, its base or its thresholds: the model page re-reads the
// model, so a switched base moves the chat to the other agent, and Fine tuning re-reads
// the ensembling.
export const MODEL_REFRESH_KEY = 'model_refresh';

/** Whether this activity is the agent saying it changed the model's rules. */
export function isRulesRefreshActivity(a: { key?: unknown }): boolean {
  return a?.key === RULES_REFRESH_KEY;
}

/** Whether this activity is the agent saying it changed the model's base or tiers. */
export function isModelRefreshActivity(a: { key?: unknown }): boolean {
  return a?.key === MODEL_REFRESH_KEY;
}

/** Whether this activity is the agent saying it changed a model in the list. */
export function isModelsRefreshActivity(a: { key?: unknown }): boolean {
  return a?.key === MODELS_REFRESH_KEY;
}

/** Whether this activity is the agent saying it wrote the signals. */
export function isSignalsRefreshActivity(a: { key?: unknown }): boolean {
  return a?.key === SIGNALS_REFRESH_KEY;
}

/**
 * The tree a refresh activity names, or null when it names none.
 *
 * Null is not a failure: it means refresh whatever tree is open. An agent that
 * knows which tree it wrote should say so, and one that does not should still be
 * able to ask.
 */
export function treeRefreshFromActivity(a: {
  key?: unknown;
  target_type?: unknown;
  target_id?: unknown;
}): { treeId: number | null } | null {
  if (a?.key !== TREE_REFRESH_KEY) return null;
  if (a.target_type !== 'tree') return { treeId: null };
  const id = Number(a.target_id);
  return { treeId: Number.isInteger(id) && id > 0 ? id : null };
}

export function jumpFromActivity(a: {
  key?: unknown;
  label?: unknown;
  target_type?: unknown;
  target_id?: unknown;
}): UiActionEvent | null {
  if (a?.key !== STUDIO_JUMP_KEY) return null;
  const targetType = typeof a.target_type === 'string' ? a.target_type : '';
  const targetId = typeof a.target_id === 'string' ? a.target_id : '';
  if (!targetType || !targetId) return null;
  return {
    target_type: targetType,
    target_id: targetId,
    label: typeof a.label === 'string' ? a.label : '',
  };
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  // Jump-to chips reconstructed from the assistant turn's persisted steps, so they
  // reappear when a past conversation is reopened (not just live).
  actions?: UiActionEvent[];
  // Reference stubs for the blocks the turn produced: key, kind and title, never the
  // body. rgip strips the raw blocks out of the stored prose deliberately and serves
  // their content through the deliverables API, so a caller that wants to RENDER one
  // resolves it by `key` against `listDeliverables` (see `artifact_key`).
  deliverables?: DeliverableRef[];
}

/** What a transcript message says about a block it produced. */
export interface DeliverableRef {
  key: string;
  kind: string;
  title: string;
}

interface RawStep {
  key?: unknown;
  label?: unknown;
  target_type?: unknown;
  target_id?: unknown;
}

interface RawThreadMessage {
  role?: unknown;
  content?: unknown;
  steps?: unknown;
  deliverables?: unknown;
}

// Get a thread's prior transcript so a reopened chat panel can render history.
// Reached via Studio's /api/copilot proxy.
export async function getTranscript(
  tenant: string,
  threadId: string
): Promise<ChatMessage[]> {
  const response = await fetch(
    `${BASE}/v1/threads/${encodeURIComponent(
      threadId
    )}/messages?tenant=${encodeURIComponent(tenant)}`,
    { credentials: 'same-origin' }
  );
  // A fresh model has no thread row yet — the row is only created on a successful
  // first turn — so a 404 here just means "no history yet", not a real failure.
  if (response.status === 404) return [];
  if (!response.ok) throw new Error(await errorMessage(response));
  // rgip's ThreadMessages carries `{ thread_id, agent, messages }`, where each
  // message may also have `steps`/`deliverables`/`run_id` — read defensively and
  // keep only what this read-only client needs.
  const doc = (await response.json()) as { messages?: RawThreadMessage[] };
  const messages = Array.isArray(doc?.messages) ? doc.messages : [];
  return messages.map((m) => {
    // Assistant turns carry `steps` (the tool/activity trace); the `studio_jump`
    // ones rebuild the jump-to chips so they survive a reopen.
    const steps = Array.isArray(m?.steps) ? (m.steps as RawStep[]) : [];
    const actions = steps
      .map((s) => jumpFromActivity(s))
      .filter((a): a is UiActionEvent => a !== null);
    const refs = Array.isArray(m?.deliverables)
      ? (m.deliverables as Record<string, unknown>[])
          .filter((d) => d && typeof d.key === 'string')
          .map((d) => ({
            key: String(d.key),
            kind: typeof d.kind === 'string' ? d.kind : '',
            title: typeof d.title === 'string' ? d.title : '',
          }))
      : [];
    return {
      role: (m?.role === 'user' ? 'user' : 'assistant') as 'user' | 'assistant',
      content: typeof m?.content === 'string' ? m.content : '',
      ...(actions.length ? { actions } : {}),
      ...(refs.length ? { deliverables: refs } : {}),
    };
  });
}

// A product activity from the chat stream: server-owned key + label; `id` (the tool
// call id) correlates start and end. Internal tool names never cross the wire. A
// `studio_jump` activity also carries target_type/target_id (see jumpFromActivity).
export interface ChatActivityEvent {
  id?: string;
  key: string;
  label: string;
  target_type?: string;
  target_id?: string;
  /**
   * On an `activity_end`: what the finished call actually did, server-written.
   *
   * NESTED, and it matters. The activity's own `target_id` above is the TREE, which is
   * what a refresh has to name; the receipt's is the NODE that now carries the split,
   * which is what a jump has to name. Two targets doing two jobs, so reading `detail`
   * off the top level finds nothing at all: rgip has never put it there.
   */
  receipt?: ChatReceipt;
}

/** What rgip's `result_activity` sends on the end of a tree write. */
export interface ChatReceipt {
  /**
   * The write in one line. The start event is built from the tool's ARGUMENTS, so it
   * can say a split is being applied and never which one; the condition, its lift and
   * coverage, and the node now carrying it exist only in the result.
   */
  detail?: string;
  target_type?: string;
  target_id?: string;
  /**
   * The same write as something to RENDER: the candidate applied, the reason given,
   * what it was ranked against, and the leaves it created. See `splitCardData.ts`.
   *
   * Additive to `detail`. A refused write carries neither.
   */
  card?: unknown;
}

export interface ChatHandlers {
  // The turn's user message, echoed as the run's first frame — lets a client that
  // re-attached after a refresh (having lost its optimistic bubble) re-show it.
  onInput?: (message: string) => void;
  onToken?: (delta: string) => void;
  // A delta of the model's own thinking, when the model emits it (rgip streams
  // Pydantic AI ThinkingParts as `reasoning` frames). Optional: a handler-less
  // client just never shows it.
  onReasoning?: (delta: string) => void;
  onActivityStart?: (event: ChatActivityEvent) => void;
  onActivityEnd?: (event: ChatActivityEvent) => void;
  // A deliverable block the agent wrote mid-answer (rgip ADR 0006). The prose the
  // block was embedded in arrives as `token` frames either side of these, in
  // order, so a client that keeps that order gets the block where the agent put
  // it. Content only ever reaches the client through `onArtifactDelta`.
  onArtifactStart?: (event: ArtifactStartEvent) => void;
  onArtifactDelta?: (event: { key: string; delta: string }) => void;
  onArtifactEnd?: (event: { key: string }) => void;
  // The block, persisted. Fires after its `artifact_end` and before the terminal
  // message, and carries META ONLY (`deliverable_id`, `artifact_key`, `version`,
  // `created_at`, ...) with no `content`: that column is on the detail row, read
  // back by id. So this is the handle for later, not a second copy of the body.
  onArtifact?: (event: PersistedArtifactEvent) => void;
  /**
   * The finished run, served whole instead of streamed.
   *
   * rgip yields a `snapshot` frame when a client attaches to a run whose live
   * buffer is gone: the turn already ended, so there is nothing to tail and the
   * durable projection is sent instead (`app/api/v1/runs.py`). Optional, because
   * the dispatcher already replays its `text` through `onToken` for any client
   * that received none, which is what makes a fast turn render at all.
   */
  onSnapshot?: (snapshot: {
    status?: string;
    text?: string;
    error?: string | null;
  }) => void;
  onError?: (message: string) => void;
}

export interface ArtifactStartEvent {
  key: string;
  kind: string;
  title: string;
  format?: string;
}

export interface PersistedArtifactEvent {
  deliverable_id: string;
  artifact_key: string;
  kind?: string;
  title?: string;
  version?: number;
}

// Read an SSE body, invoking `dispatch` on each \n\n-delimited frame.
async function pumpSse(
  body: ReadableStream<Uint8Array>,
  dispatch: (raw: string) => void
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let boundary: number;
    while ((boundary = buffer.indexOf('\n\n')) !== -1) {
      dispatch(buffer.slice(0, boundary));
      buffer = buffer.slice(boundary + 2);
    }
  }
  if (buffer.trim()) dispatch(buffer);
}

// Map chat SSE frames onto the handlers. Exported (rather than kept private, as
// admin's editorDispatch is) so it can be unit-tested directly against hand-built
// SSE text without mocking fetch/ReadableStream. This agent is read-only, so —
// unlike admin's editorDispatch — there is no model_updated/model_edited handling.
export function chatDispatch(handlers: ChatHandlers): (raw: string) => void {
  // Per STREAM, not per frame: whether any prose arrived live, so a late
  // `snapshot` knows whether replaying its text would duplicate it.
  let streamed = false;
  return (raw: string) => {
    let name = '';
    let data = '';
    for (const line of raw.split('\n')) {
      if (line.startsWith('event:')) name = line.slice(6).trim();
      else if (line.startsWith('data:')) data += line.slice(5).trim();
    }
    if (!data) return;
    const p = JSON.parse(data);
    if (name === 'input') handlers.onInput?.(p.message as string);
    else if (name === 'token') {
      streamed = true;
      handlers.onToken?.(p.delta as string);
    } else if (name === 'snapshot') {
      // A turn that finished before the client attached. Short runs lose the
      // race every time: the buffer is gone by the time `startTurn` has returned
      // and the stream request has gone out, so the whole answer arrives here and
      // nowhere else. Dropped, it renders as an empty reply to a question the
      // agent actually answered.
      //
      // Replayed through `onToken` only when nothing streamed, so a client that
      // already has the prose does not get a second copy appended to it.
      const text = typeof p.text === 'string' ? p.text : '';
      if (!streamed && text) handlers.onToken?.(text);
      handlers.onSnapshot?.(
        p as { status?: string; text?: string; error?: string }
      );
      if (p.status === 'error' && p.error) handlers.onError?.(String(p.error));
    } else if (name === 'reasoning') handlers.onReasoning?.(p.delta as string);
    else if (name === 'activity_start')
      handlers.onActivityStart?.(p as ChatActivityEvent);
    else if (name === 'activity_end')
      handlers.onActivityEnd?.(p as ChatActivityEvent);
    else if (name === 'artifact_start')
      handlers.onArtifactStart?.(p as ArtifactStartEvent);
    else if (name === 'artifact_delta')
      handlers.onArtifactDelta?.(p as { key: string; delta: string });
    else if (name === 'artifact_end')
      handlers.onArtifactEnd?.(p as { key: string });
    else if (name === 'artifact')
      handlers.onArtifact?.(p as PersistedArtifactEvent);
    else if (name === 'error')
      handlers.onError?.((p.message as string) ?? 'Chat failed');
  };
}

// Start a chat turn as a durable background run (kind=chat): the rgip worker drives
// the inbound-model-builder turn and checkpoints it to the session thread, so the
// turn finishes + persists even if the client detaches. Returns the run_id to
// attach to.
/** The `context.origin` rgip reads to know the turn is the agent's own suggestion. */
export const SUGGESTED_STEP = 'suggested_step';

export async function startTurn(
  tenant: string,
  threadId: string,
  /**
   * The model in focus, or null for an agent whose subject is the TENANT.
   *
   * Null omits the key rather than sending an empty one: `model_id: ""` reads as a
   * model that is there and is blank, and an agent given one has to decide whether
   * to trust it. `studio-home` answers across every model and has no single subject.
   */
  modelId: string | null,
  message: string,
  opts: {
    agent?: string;
    context?: Record<string, unknown>;
    /**
     * The tenant's AI flag (`studio-ai-model-insights`), for a turn a person asked (typed or
     * clicked). Sent as `context.memory: true`, the only thing that makes approved
     * memory active for a run in rgip (ADR 0027). Off or absent sends no key at all, so
     * the payload is what it was before Memory existed. Automated runs never set it.
     */
    memory?: boolean;
    /**
     * Set when the message is one the agent itself offered (a pressed `choices`
     * option) rather than something the person wrote. rgip skips its help center
     * search for these. Typed turns pass nothing, so their payload is unchanged.
     */
    origin?: typeof SUGGESTED_STEP;
  } = {}
): Promise<string> {
  const response = await fetch(
    `${BASE}/v1/runs?tenant=${encodeURIComponent(tenant)}`,
    {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        kind: 'chat',
        agent: opts.agent ?? TREES_AGENT,
        message,
        thread_id: threadId,
        // model_id is always in focus; a follow-up turn also passes the cached
        // model_summary so the answering agent has it as context (see rgip
        // render_model_summary).
        context: {
          ...(modelId === null ? {} : { model_id: modelId }),
          ...opts.context,
          ...(opts.memory === true ? { memory: true } : {}),
          ...(opts.origin ? { origin: opts.origin } : {}),
        },
      }),
    }
  );
  if (!response.ok) throw new Error(await errorMessage(response));
  const doc = (await response.json()) as { run_id: string };
  return doc.run_id;
}

// The structured AI Model Summary the `model-summary` agent emits as a
// `model_summary` deliverable. Shape mirrors the agent's JSON contract.
// `tree` + `node` (when present) deep-link a line to the node it was read from:
// tree = the tree number (tree_N), node = that node's `id` in the tree structure.
//
// EVERY field is optional and the renderer treats a missing one as "omit the
// section". Summaries are PERSISTED deliverables reused on reopen, so older cached
// ones predate the fields added below and must keep rendering rather than blanking
// the card. `rate` and `focus` are exactly that: no longer emitted, still read.
export interface ModelSummaryPoint {
  point?: string;
  /** @deprecated Superseded by `lift`; still present on cached summaries. */
  rate?: string;
  lift?: string;
  tree?: number;
  node?: number;
}

// Which magnitude a point line shows. Current summaries carry `lift` alone, so it
// takes the pill; cached ones carry `rate` (pill) with `lift` as a parenthetical
// aside. Kept here beside the interface, and pure, so the "a field the agent sent
// silently never rendered" failure is unit-testable.
export function pointMagnitude(point: ModelSummaryPoint): {
  pill?: string;
  aside?: string;
} {
  if (point.rate && point.lift) return { pill: point.rate, aside: point.lift };
  return { pill: point.rate ?? point.lift };
}

// Where the model's scores are actively pushed. The agent copies this object
// through and nothing else (rgip #261): the sentence and the link to the admin
// app's push settings are built here, so the copy can be reworded without an agent
// deploy. `connectors` carries display names, already deduplicated, filtered to the
// known-active ones and ordered by the source layer. `custom_script` means a
// hand-written script may override the push config we read, so the row keeps the
// list but qualifies it rather than asserting something it cannot stand behind.
//
// New summaries ALWAYS carry the key, with `{connectors: []}` when there is
// nothing to report. Empty therefore has to behave exactly like the absent key a
// cached summary has: no row, no link. Both go through exportsSentence, which
// returns null for either, so neither the payload nor the renderer has a special
// case for "nothing configured".
export interface ModelSummaryExports {
  connectors: string[];
  custom_script?: boolean;
}

// How the model was built: what it predicts, the training window, where it exports.
export interface ModelSummarySetup {
  predicts?: string;
  // What the conversion actually COUNTS, as its own field rather than spliced
  // into the `predicts` sentence: "a Salesforce opportunity at Qualification or
  // later". Already written and safe to display, so it renders verbatim, no
  // parsing and no reformatting. Null or absent is the COMMON case, most models
  // have no describable conversion rule, and then nothing renders at all.
  conversion?: string | null;
  trained_on?: string;
  exports?: ModelSummaryExports | null;
  /**
   * @deprecated No longer emitted (the panel beside the card already dates the
   * model); still present on cached summaries.
   */
  last_deployed?: string;
  /**
   * @deprecated No longer emitted (live/primary/archived is a badge in the page
   * header); still present on cached summaries.
   */
  status?: string;
}

// The Exports row's sentence. Kept here beside the interface, and pure, so the join
// and every do-not-render case are unit-testable.
//
// Returns null for a missing key, an explicit null and an empty connector list
// alike. A failed read upstream is deliberately indistinguishable from "nothing
// configured", so the card must render NO row rather than invent a line claiming
// the model exports nowhere.
export function exportsSentence(
  exports?: ModelSummaryExports | null
): string | null {
  const names = (exports?.connectors ?? []).filter(
    (name) => typeof name === 'string' && !!name.trim()
  );
  if (!names.length) return null;
  const list =
    names.length === 1
      ? names[0]
      : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return exports?.custom_script
    ? `Scores are actively pushed to ${list}, custom config in place.`
    : `Scores are actively pushed to ${list}.`;
}

// Sentinels the dataset variables use to mean "no audience filter". They are not
// the names of configured audiences, so a deep link built from one points at a
// screen that does not exist.
// Score concentration, plus the standing caveat that these come from the
// rebalanced training set rather than live traffic.
export interface ModelSummaryPerformance {
  concentration?: string;
  caveat?: string;
}

export interface ModelSummary {
  setup?: ModelSummarySetup;
  targets?: string;
  /** @deprecated Folded into `targets`; still present on cached summaries. */
  focus?: string;
  // A condition every point in the list shares, pulled out of the individual lines
  // so they don't all repeat it. The agent always emits the key, null when there is
  // no shared condition, so `null` here means "none" rather than "not supported".
  raises_shared?: string | null;
  raises?: ModelSummaryPoint[];
  lowers_shared?: string | null;
  lowers?: ModelSummaryPoint[];
  performance?: ModelSummaryPerformance;
}

// A stored summary plus when it was written. `generatedAt` comes from the
// deliverable row, not from the summary JSON, so it stays right even for
// cached summaries produced before the field existed.
export interface StoredModelSummary {
  summary: ModelSummary;
  generatedAt?: string;
}

// The persisted model_summary deliverable for a thread, or null if none yet.
// This IS the cache: the deliverable is persisted server-side, so a reopened
// model page reuses it instead of re-running the summary.
export async function fetchModelSummary(
  tenant: string,
  threadId: string
): Promise<StoredModelSummary | null> {
  const list = await fetch(
    `${BASE}/v1/deliverables?tenant=${encodeURIComponent(
      tenant
    )}&thread_id=${encodeURIComponent(threadId)}`,
    { credentials: 'same-origin' }
  );
  if (list.status === 404) return null;
  if (!list.ok) throw new Error(await errorMessage(list));
  const doc = (await list.json()) as {
    deliverables?: { deliverable_id: string; created_at?: string }[];
  };
  const head = doc.deliverables?.[0];
  if (!head?.deliverable_id) return null;
  const detail = await fetch(
    `${BASE}/v1/deliverables/${encodeURIComponent(
      head.deliverable_id
    )}?tenant=${encodeURIComponent(tenant)}`,
    { credentials: 'same-origin' }
  );
  if (!detail.ok) throw new Error(await errorMessage(detail));
  const rec = (await detail.json()) as { content?: string };
  if (!rec.content) return null;
  try {
    return {
      summary: JSON.parse(rec.content) as ModelSummary,
      generatedAt: head.created_at,
    };
  } catch {
    return null;
  }
}

// The structured `tree-diagnostics` deliverable. Shape mirrors the agent's JSON
// contract on rgip `feat/tree-diagnostics`: the agent authors only `verdict.headline`,
// `verdict.detail` and each finding's `title`, `detail` and `fix`, and copies every
// other value from the tool byte for byte. So anything numeric here was counted by
// `app/sources/springbok/diagnostics.py`, never estimated by a model.
/** One node a finding covers, with the leads sitting in it. */
export interface TreeFindingNode {
  node: number;
  population?: number;
}

export interface TreeFinding {
  id: string;
  code?: string;
  severity: 'blocker' | 'warning' | 'opportunity';
  title: string;
  detail?: string;
  fix?: string;
  /**
   * What the reader does about it, and therefore what the panel offers.
   *
   * `split` divides a group that is big enough to divide. `remove_split` covers a cut
   * that produced a group too small to trust, or that separates nothing.
   * `change_split` covers a cut on a dangerous or repeated column.
   *
   * It decides the WORDING and whether a delete is offered. It deliberately does not
   * decide whether a candidate inserts or replaces: the tree's own shape settles that,
   * because a review can be stale and the cost of trusting a stale one is a duplicated
   * subtree. Absent on deliverables cached before rgip started sending it.
   */
  action?: 'split' | 'remove_split' | 'change_split';
  /** Tree number and node id, which are what make a finding a link to its node. */
  tree?: number;
  node?: number;
  /**
   * Every node the finding is about, worst first, one entry even when there is only
   * one node. `node` holds the first of them. Absent on deliverables cached before
   * rgip started sending it.
   */
  nodes?: TreeFindingNode[];
  evidence?: Record<string, unknown>;
}

/** One ranked next-split option for a node, as rgip computed it. */
export interface TreeSplitCandidate {
  /**
   * What identifies this cut, derived by rgip from the column, operator, values and
   * band. Stable across runs and not positional, so it is the join between a candidate
   * and the sentence the agent wrote for it. Absent on deliverables cached before it
   * existed.
   */
  id?: string;
  /**
   * The agent's own sentence for this split: "Companies that do not use Apache HTTP
   * Server". Authored prose, merged onto the candidate by id after the run, and the
   * only field here a model writes. The structured fields below stay rgip's, because
   * they are what the applied split is rebuilt from.
   *
   * Absent when the agent wrote nothing for this candidate, which is every candidate
   * past the third of a node and every deliverable cached before this existed. The
   * renderer then describes the structured fields as it always did.
   */
  phrase?: string;
  /** An ordinal run said as a span: "4-pre-2010 and above". Bands only. */
  band?: string;
  column?: string;
  /**
   * The column as a person would say it: "HQ Country", "IT spend", "B2B enterprise".
   * rgip sends it out of the label dictionary it keeps in sync with studio-cli. Absent
   * on deliverables cached before rgip started sending it, and the UI then derives a
   * name from `column` as it did before.
   */
  label?: string;
  /**
   * The comparison, structured. Present alongside `condition`, which is assembled
   * for a human and is not SQL, so only these two can be described in the UI's own
   * words.
   */
  operator?: 'in' | '>=' | '=' | 'is not';
  /**
   * How the engine read the column. `single_value` marks a column where one record
   * carries several values, and it decides whether equality or `contains` is the
   * faithful test. Dropping it is what applied a technographic split as string
   * equality against a whole array and emptied one side.
   */
  kind?: 'numeric' | 'ordinal_band' | 'categorical' | 'single_value';
  values?: (string | number)[];
  condition?: string;
  lift?: number | null;
  coverage_pct?: number;
  true_population?: number;
  true_rate_pct?: number;
  information_gain?: number;
  p_value?: number;
  /** Whether no tree in the model splits on this column yet. */
  new_to_tree?: boolean;
}

export interface TreeSplitCandidateNode {
  tree?: number;
  node?: number;
  population?: number;
  condition?: string;
  candidates?: TreeSplitCandidate[];
  considered?: number;
  dropped?: number;
  /** Set when the node's statistics could not be scoped, so there is nothing to show. */
  unavailable?: string;
}

export interface TreeDiagnostics {
  verdict?: { headline?: string; detail?: string };
  performance?: {
    basis?: string;
    headline?: { leads_pct?: number; conversions_pct?: number };
    baseline_rate?: number;
    reason?: string;
  };
  counts?: Partial<Record<TreeFinding['severity'], number>>;
  findings?: TreeFinding[];
  /**
   * Ranked next splits for the leaves with room, computed by rgip from Studio's own
   * per-node statistics. This is the authority for what to split on next: the
   * insights table lower down computes candidates too, from the same engine rules but
   * a separate implementation, and one page cannot show two different answers to the
   * same question.
   */
  split_candidates?: {
    basis?: string;
    nodes?: TreeSplitCandidateNode[];
    not_examined?: { tree?: number; node?: number }[];
  };
  generated_for?: { model_id?: string; tree_fingerprint?: string };
}

/** Deterministic per model, matching the `model-summary:{id}` convention. */
export function treeDiagnosticsThread(modelId: string): string {
  return `tree-diagnostics:${modelId}`;
}

// The persisted deliverable for a thread, with when it was generated, or null if
// there is none yet. This IS the cache: a reopened tree page reuses it instead of
// spending another model call.
export async function fetchTreeDiagnostics(
  tenant: string,
  threadId: string
): Promise<{ diagnostics: TreeDiagnostics; generatedAt: string } | null> {
  const list = await fetch(
    `${BASE}/v1/deliverables?tenant=${encodeURIComponent(
      tenant
    )}&thread_id=${encodeURIComponent(threadId)}`,
    { credentials: 'same-origin' }
  );
  if (list.status === 404) return null;
  if (!list.ok) throw new Error(await errorMessage(list));
  const doc = (await list.json()) as { deliverables?: DeliverableSummary[] };
  const latest = doc.deliverables?.[0];
  if (!latest?.deliverable_id) return null;
  const detail = await fetch(
    `${BASE}/v1/deliverables/${encodeURIComponent(
      latest.deliverable_id
    )}?tenant=${encodeURIComponent(tenant)}`,
    { credentials: 'same-origin' }
  );
  if (!detail.ok) throw new Error(await errorMessage(detail));
  const rec = (await detail.json()) as { content?: string };
  if (!rec.content) return null;
  try {
    return {
      diagnostics: JSON.parse(rec.content) as TreeDiagnostics,
      generatedAt: latest.created_at,
    };
  } catch {
    // A half-written or malformed deliverable is treated as absent, so the panel
    // regenerates rather than rendering a broken review.
    return null;
  }
}

// Kick off a diagnosis. The run is durable: the worker finishes even if the client
// detaches, so a refresh mid-run COULD re-attach through `findActiveRun` rather than
// starting a second one.
/**
 * A review is a fresh look at the tree as it stands, never a continuation.
 *
 * The thread id is stable, because it keys the cached deliverable, so without the
 * reset every re-review inherits the previous one's tool output and answer. The agent
 * then has a complete-looking diagnosis already in context and stops calling the tool:
 * observed on a real run, two turns with ZERO tool activity, producing a review of a
 * tree that had been edited since. It reported "nothing to flag" on a tree its own
 * verdict said had four undersized leaves, because it was reciting an older turn.
 *
 * Deleting the thread clears the checkpointer but not the deliverable rows, which live
 * in a separate table with no cascade, so the cached review and its version chain
 * survive. Same fix, and the same reasoning, as the model summary.
 */
export async function runTreeDiagnostics(
  tenant: string,
  modelId: string
): Promise<string> {
  const thread = treeDiagnosticsThread(modelId);
  await resetThread(tenant, thread);
  return startTurn(
    tenant,
    thread,
    modelId,
    "Review this model's decision trees.",
    {
      agent: 'tree-diagnostics',
    }
  );
}

// Wipe a thread's server-side conversation (the LangGraph checkpointer) so the next
// run on it starts with no history. Used before a summary refresh: the summary thread
// id is STABLE (it keys the cached deliverable), so it accumulates every prior
// generation's tool output + answer — which a plain re-run would echo, carrying stale
// phrasings forward. Deleting the thread clears that memory but NOT the deliverable rows
// (a separate table, no cascade), so the cached summary and its version chain survive.
// A 404 just means there's no thread yet — nothing to reset.
/**
 * Continue a conversation under another agent, in a new thread seeded with a brief.
 *
 * For a model whose base changed: its conversation moves between the tree builder and
 * the rules builder. rgip starts a new thread rather than reusing this one, because this
 * one's history holds the old agent's tool calls, and leaves this one as it was. Returns
 * the new thread's id.
 */
export async function handoffThread(
  tenant: string,
  threadId: string,
  agent: string
): Promise<string> {
  const response = await fetch(
    `${BASE}/v1/threads/${encodeURIComponent(
      threadId
    )}/handoff?tenant=${encodeURIComponent(tenant)}`,
    {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ agent }),
    }
  );
  if (!response.ok) throw new Error(`handoff failed: ${response.status}`);
  const body = (await response.json()) as { thread_id?: string };
  if (!body?.thread_id) throw new Error('handoff returned no thread');
  return body.thread_id;
}

export async function resetThread(
  tenant: string,
  threadId: string
): Promise<void> {
  const response = await fetch(
    `${BASE}/v1/threads/${encodeURIComponent(
      threadId
    )}?tenant=${encodeURIComponent(tenant)}`,
    { method: 'DELETE', credentials: 'same-origin' }
  );
  if (response.status === 404) return;
  if (!response.ok) throw new Error(await errorMessage(response));
}

// Three separate components ask for this flag on one page render (the summary
// card, the artifacts card, and the Overview layout, which needs to know whether
// the left column will be empty). Share the in-flight promise so they cost one
// request, and keep it for the life of the tab: the flag decides page structure,
// so re-resolving it mid-session would reshuffle the layout under the user.
const aiInsightsEnabled = new Map<string, Promise<boolean>>();

// Per-tenant gate for the whole AI insights block. Studio-native endpoint (not the
// copilot proxy). Fail-closed to false so the block just doesn't render on any error.
export function fetchAiInsightsEnabled(tenant: string): Promise<boolean> {
  const cached = aiInsightsEnabled.get(tenant);
  if (cached) return cached;
  const pending = (async () => {
    try {
      const r = await fetch(
        `/api/tenant/${encodeURIComponent(tenant)}/ai_insights_enabled`,
        { credentials: 'same-origin' }
      );
      if (!r.ok) return false;
      const doc = (await r.json()) as { enabled?: boolean };
      return !!doc.enabled;
    } catch {
      return false;
    }
  })();
  aiInsightsEnabled.set(tenant, pending);
  return pending;
}

// Same shape, different question, and the cache matters for the same reason: the tree
// page asks once and the answer decides whether three sections exist at all.
const treeInsightsEnabled = new Map<string, Promise<boolean>>();

/**
 * Master gate for the tree page's insights: the performance charts, the manual attribute
 * panel and the AI review. Fail-closed to false, so on any error the page renders the way
 * it did before any of them rather than half of them.
 *
 * The AI review is nested under this one and ALSO checks `fetchAiInsightsEnabled`, so
 * turning this off removes everything while the AI flag still narrows the review alone.
 */
export function fetchTreeInsightsEnabled(tenant: string): Promise<boolean> {
  // Answered before the cache and before the request: someone who has opted out
  // of the revamp should look exactly like a tenant without the flag, and the
  // cheapest way to be exactly that is to give every caller the same `false`
  // they would have got. Ahead of the cache so the answer cannot be poisoned by
  // a resolution from before the toggle was flipped.
  if (legacyForced(tenant)) return Promise.resolve(false);
  const cached = treeInsightsEnabled.get(tenant);
  if (cached) return cached;
  const pending = (async () => {
    try {
      const r = await fetch(
        `/api/tenant/${encodeURIComponent(tenant)}/tree_insights_enabled`,
        { credentials: 'same-origin' }
      );
      if (!r.ok) return false;
      const doc = (await r.json()) as { enabled?: boolean };
      return !!doc.enabled;
    } catch {
      return false;
    }
  })();
  treeInsightsEnabled.set(tenant, pending);
  return pending;
}

// Cached for the life of the tab like the two above: the model chat and Ask Data Studio
// both ask, and the answer decides whether a header control exists at all.
const memoryEnabled = new Map<string, Promise<boolean>>();

/**
 * Gate for the assistant's Memory in both chats: the proposals banner and the Memory
 * view. Fail-closed to false, so on any error the chats render as they did before it.
 *
 * Not subject to the legacy opt-out: that declines the tree page revamp, and the chat
 * this lives in is gated by `fetchAiInsightsEnabled`, which does not read it either.
 */
export function fetchMemoryEnabled(tenant: string): Promise<boolean> {
  const cached = memoryEnabled.get(tenant);
  if (cached) return cached;
  const pending = (async () => {
    try {
      const r = await fetch(
        `/api/tenant/${encodeURIComponent(tenant)}/memory_enabled`,
        { credentials: 'same-origin' }
      );
      if (!r.ok) return false;
      const doc = (await r.json()) as { enabled?: boolean };
      return !!doc.enabled;
    } catch {
      return false;
    }
  })();
  memoryEnabled.set(tenant, pending);
  return pending;
}

// A persisted deliverable (the reports are deliverables). One row per version of a
// (thread, artifact_key) chain — so regenerating a report adds a new one, not replaces.
export interface DeliverableSummary {
  deliverable_id: string;
  // The agent-chosen `key` from the deliverable tag, which is what a transcript's
  // reference stubs are keyed by. rgip has always sent it; this interface dropped it,
  // so there was no way to resolve a stub to the row that holds its body.
  artifact_key: string;
  thread_id: string;
  kind: string;
  title: string;
  version: number;
  created_at: string;
}

// The latest deliverable of each chain on a thread (the list endpoint collapses to
// latest-per-chain). Empty when nothing's been generated for the thread.
export async function listDeliverables(
  tenant: string,
  threadId: string
): Promise<DeliverableSummary[]> {
  const r = await fetch(
    `${BASE}/v1/deliverables?tenant=${encodeURIComponent(
      tenant
    )}&thread_id=${encodeURIComponent(threadId)}`,
    { credentials: 'same-origin' }
  );
  if (r.status === 404) return [];
  if (!r.ok) throw new Error(await errorMessage(r));
  const doc = (await r.json()) as { deliverables?: DeliverableSummary[] };
  return doc.deliverables ?? [];
}

// Every version of a deliverable's chain, newest first — this is how one report shows
// up multiple times (one per generation), each with its own created_at.
export async function fetchDeliverableVersions(
  tenant: string,
  deliverableId: string
): Promise<DeliverableSummary[]> {
  const r = await fetch(
    `${BASE}/v1/deliverables/${encodeURIComponent(
      deliverableId
    )}/versions?tenant=${encodeURIComponent(tenant)}`,
    { credentials: 'same-origin' }
  );
  if (r.status === 404) return [];
  if (!r.ok) throw new Error(await errorMessage(r));
  const doc = (await r.json()) as { versions?: DeliverableSummary[] };
  return doc.versions ?? [];
}

/**
 * A stored deliverable's body, by id, fetched lazily.
 *
 * The `artifact` SSE event carries meta only, so this is the only way to see a block
 * as the server stored it. That matters for any block whose content is merged from a
 * tool's `facts` after the model wrote it: the stream has the model's fragment and
 * this has the whole thing.
 *
 * Named for what it returns rather than for one format. The previous name said HTML,
 * had no callers at all, and described the single shape it happened to be written for;
 * the column is just `content`, and a JSON block reads it exactly the same way.
 */
export async function fetchDeliverableContent(
  tenant: string,
  deliverableId: string
): Promise<string> {
  const r = await fetch(
    `${BASE}/v1/deliverables/${encodeURIComponent(
      deliverableId
    )}?tenant=${encodeURIComponent(tenant)}`,
    { credentials: 'same-origin' }
  );
  if (!r.ok) throw new Error(await errorMessage(r));
  const rec = (await r.json()) as { content?: string };
  return rec.content ?? '';
}

/** One of this user's conversations, as the thread index lists it. */
export interface ThreadRow {
  thread_id: string;
  /** Which builder the conversation ran under, which says whether it was about the
   *  model as trees or as rules. */
  agent?: string;
  title?: string | null;
  preview?: string | null;
  /** `handed_from`: the conversation a base switch handed this one over from. */
  metadata?: { handed_from?: string };
  updated_at: string;
}

/**
 * This user's conversations about one model, newest first.
 *
 * The panel resumes the first of these instead of minting a thread per mount, which
 * is what makes a conversation survive a refresh, a navigation away and back, and the
 * copy page's own reload when duplication finishes. Per user and per tenant: `mine`
 * is resolved from the verified token server-side, never from anything sent here.
 *
 * Best-effort by design. On any failure the caller starts a fresh conversation, which
 * is exactly what it used to do unconditionally.
 */
export async function listModelThreads(
  tenant: string,
  modelId: string
): Promise<ThreadRow[]> {
  try {
    const r = await fetch(
      `${BASE}/v1/threads?mine=true&agent=${encodeURIComponent(
        CHAT_AGENTS.join(',')
      )}&model_id=${encodeURIComponent(modelId)}&tenant=${encodeURIComponent(
        tenant
      )}`,
      { credentials: 'same-origin' }
    );
    if (!r.ok) return [];
    const doc = (await r.json()) as { threads?: ThreadRow[] };
    return Array.isArray(doc?.threads) ? doc.threads : [];
  } catch {
    return [];
  }
}

/**
 * This user's conversations with the models-list assistant, newest first.
 *
 * Per tenant and per USER, and deliberately not per model: this assistant works the
 * whole list, so "which of my models should I look at" belongs to the org and not to
 * any one of them. Its threads carry no `model_id` at all, which is why this filters
 * on the agent instead.
 *
 * Same best-effort contract as `listModelThreads`: any failure reads as "no earlier
 * conversations", because the alternative is a landing page that will not render.
 */
export async function listHomeThreads(
  tenant: string,
  agent: string
): Promise<ThreadRow[]> {
  try {
    const r = await fetch(
      `${BASE}/v1/threads?mine=true&agent=${encodeURIComponent(
        agent
      )}&tenant=${encodeURIComponent(tenant)}`,
      { credentials: 'same-origin' }
    );
    if (!r.ok) return [];
    const doc = (await r.json()) as { threads?: ThreadRow[] };
    return Array.isArray(doc?.threads) ? doc.threads : [];
  } catch {
    return [];
  }
}

// A still-running chat turn on this thread, if any — so a page refreshed mid-
// response can re-attach and keep streaming the answer. Carries the run's true
// start time so the elapsed clock continues from when the turn began, not from the
// re-attach moment. Best-effort: null on any failure (resume still works, just
// without live continuation).
export async function findActiveRun(
  tenant: string,
  threadId: string
): Promise<{ runId: string; startedAt: number } | null> {
  try {
    const response = await fetch(
      `${BASE}/v1/runs?kind=chat&thread_id=${encodeURIComponent(
        threadId
      )}&tenant=${encodeURIComponent(tenant)}`,
      { credentials: 'same-origin' }
    );
    if (!response.ok) return null;
    const doc = (await response.json()) as {
      runs: { run_id: string; status: string; created_at: string }[];
    };
    const active = doc.runs.find(
      (r) => r.status === 'queued' || r.status === 'running'
    );
    if (!active) return null;
    const startedAt = Date.parse(active.created_at);
    return {
      runId: active.run_id,
      startedAt: Number.isNaN(startedAt) ? Date.now() : startedAt,
    };
  } catch {
    return null;
  }
}

// Attach to a chat turn's event stream (replay, then live-tail). Aborting `signal`
// only DETACHES the client — the run keeps running on the worker and persists.
export async function streamRun(
  tenant: string,
  runId: string,
  handlers: ChatHandlers,
  signal?: AbortSignal
): Promise<void> {
  const response = await fetch(
    `${BASE}/v1/runs/${encodeURIComponent(
      runId
    )}/events?tenant=${encodeURIComponent(tenant)}`,
    {
      credentials: 'same-origin',
      headers: { Accept: 'text/event-stream' },
      signal,
    }
  );
  if (!response.ok || !response.body)
    throw new Error(await errorMessage(response));
  await pumpSse(response.body, chatDispatch(handlers));
}

/**
 * Actually stop a run, as opposed to detaching from it.
 *
 * Aborting `streamRun`'s signal only closes the reader: the worker carries on to
 * the end of the turn, which for a tree-building turn means it keeps writing
 * splits after the user has stopped watching. This is the only call that halts
 * the work.
 *
 * Idempotent server-side: a run that reached a terminal state during the abort
 * window keeps that state rather than being overwritten with `cancelled`, and
 * cancelling an already-terminal run is a no-op that still returns 200.
 */
export async function cancelRun(tenant: string, runId: string): Promise<void> {
  const response = await fetch(
    `${BASE}/v1/runs/${encodeURIComponent(
      runId
    )}/cancel?tenant=${encodeURIComponent(tenant)}`,
    { method: 'POST', credentials: 'same-origin' }
  );
  if (!response.ok) throw new Error(await errorMessage(response));
}

/**
 * Whether a scroll region is close enough to its end to keep following new
 * content.
 *
 * Autoscroll has to be conditional or it fights the reader: a turn can run for
 * minutes, and scrolling up to re-read an earlier answer is the main thing
 * someone does while waiting. Pure and exported so the threshold is testable
 * without a DOM.
 */
export function shouldStickToBottom(
  region: { scrollTop: number; clientHeight: number; scrollHeight: number },
  slackPx = 48
): boolean {
  const remaining =
    region.scrollHeight - region.scrollTop - region.clientHeight;
  return remaining <= slackPx;
}

// Delete a thread (the panel's "new session" reset) — purges both the thread index
// row and the underlying conversation memory server-side.
export async function deleteThread(
  tenant: string,
  threadId: string
): Promise<void> {
  const response = await fetch(
    `${BASE}/v1/threads/${encodeURIComponent(
      threadId
    )}?tenant=${encodeURIComponent(tenant)}`,
    { method: 'DELETE', credentials: 'same-origin' }
  );
  if (!response.ok) throw new Error(await errorMessage(response));
}
